import "bootstrap/dist/css/bootstrap.min.css";
import { Button, Modal } from "react-bootstrap";
import React, { useEffect, useState } from "react";
import { FaEdit, FaPlus, FaSearch, FaTrash, FaCloudUploadAlt } from "react-icons/fa";
import ProductModal from "./ProductModal";
import { Toaster } from "react-hot-toast";
import showToast from "../Component/ToastNotification";
import axios from "axios";

const ProductList = () => {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(500);
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [productId, setProductId] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadStatus, setUploadStatus] = useState("");
  const [isUploading, setIsUploading] = useState(false);

  // Fetching products
  const fetchProducts = async () => {
    const token = localStorage.getItem("authToken");
    if (token) {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/products`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (!response.ok) {
          throw new Error("Failed to fetch products");
        }

        const data = await response.json();
        if (data?.products) {
          const parsedData = data?.products.map((product) => ({
            ...product,
            ProductCategory:
            typeof product.ProductCategory === 'string'
              ? JSON.parse(product.ProductCategory)
              : product.ProductCategory,
          SubCategory:
            typeof product.SubCategory === 'string'
              ? JSON.parse(product.SubCategory)
              : product.SubCategory,
          }));

          setProducts(parsedData);
        } else setProducts([]);
      } catch (error) {
        console.error("Error fetching products:", error);
      }
    }
  };
  // Fetching Categories
  const fetchCategories = async () => {
    const token = localStorage.getItem("authToken");
    if (token) {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/get-category`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (!response.ok) {
          throw new Error("Failed to fetch categories");
        }

        const result = await response.json();

        setCategories(result.categories || []);
      } catch (error) {
        console.error("Error fetching categories:", error);
      }
    }
  };

  useEffect(() => {
    fetchProducts();
    fetchCategories();
  }, []);

  // Pagination and search
const filteredProducts = products.filter((product) => {
  const name = product?.ProductName?.toLowerCase() || "";
  const desc = product?.ProductDesc?.toLowerCase() || "";
  const category = product?.ProductCategory?.CategoryName?.toLowerCase() || "";
  const subCat = product?.SubCategory?.SubCategoryName?.toLowerCase() || "";
  const price = String(product?.ProductPrice || "").toLowerCase();
  const color = Array.isArray(product?.ProductColor)
    ? product.ProductColor.join(", ").toLowerCase()
    : String(product?.ProductColor || "").toLowerCase();

  return (
    name.includes(searchTerm.toLowerCase()) ||
    desc.includes(searchTerm.toLowerCase()) ||
    category.includes(searchTerm.toLowerCase()) ||
    subCat.includes(searchTerm.toLowerCase()) ||
    price.includes(searchTerm.toLowerCase()) ||
    color.includes(searchTerm.toLowerCase())
  );
});

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentProducts = filteredProducts.slice(
    indexOfFirstItem,
    indexOfLastItem
  );
  const totalPages = Math.ceil(filteredProducts.length / itemsPerPage);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  //For add and edit
  const handleEditClick = (product) => {
    setSelectedProduct(product);
    setShowModal(true);
  };
  const handleAddClick = () => {
    setSelectedProduct(null);
    setShowModal(true);
  };
  const handleSaveProduct = async (formData, isEditing) => {
    try {
      const token = localStorage.getItem("authToken");

      const url = isEditing
        ? `${process.env.REACT_APP_API_URL}/update-product`
        : `${process.env.REACT_APP_API_URL}/add-product`;

      const response = await fetch(url, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData, // ✅ Send FormData directly (including images)
      });

      if (!response.ok) {
        throw new Error(
          isEditing ? "Failed to update product" : "Failed to add product"
        );
      }

      const result = await response.json();

      if (isEditing) {
        setProducts(
          products.map((prod) =>
            prod.product_id === formData.get("ProductId") ? result.data : prod
          )
        );
      } else {
        setProducts([...products, result.data]);
      }
      showToast(
        isEditing
          ? "Product updated successfully!"
          : "Product added successfully!"
      );

      fetchProducts();
      setShowModal(false);
    } catch (error) {
      console.error("Error adding/updating product:", error);
      showToast("Something went wrong!", "error");
    }
  };

  // For delete
  const handleDeleteClick = (productId) => {
    setProductId(productId);
    setShowDeleteModal(true);
  };
  const handleDeleteProduct = async () => {
    const token = localStorage.getItem("authToken");
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/delete-product`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ ID: productId }),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to delete product");
      }
      showToast("Category deleted successfully!");
      fetchProducts();
      setProducts(
        products.filter((product) => product.product_id !== productId)
      );
      setShowDeleteModal(false);
    } catch (error) {
      console.error("Error deleting product:", error);
      showToast("Failed to delete category!", "error");
    }
  };

  // Handle Excel file selection
  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file && file.name.endsWith(".xlsx")) {
      setSelectedFile(file);
      setUploadStatus(""); // Reset status on new file select
    } else {
      alert("Please upload a valid Excel file.");
    }
  };

  // Handle file upload
  const handleFileUpload = async () => {
    if (!selectedFile) {
      alert("Please select an Excel file first.");
      return;
    }

    setIsUploading(true);
    setUploadStatus("Uploading...");

    const formData = new FormData();
    formData.append("file", selectedFile);

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/products/bulk-upload`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      setUploadStatus("Upload successful");
      showToast("Products uploaded successfully!");
      setSelectedFile(null);
      fetchProducts(); // Refresh products list after upload
    } catch (error) {
      setUploadStatus("Upload failed");
      console.error("Upload error:", error);
      showToast("Something went wrong during upload", "error");
    } finally {
      setIsUploading(false);
    }
  };

  const renderListField = (value) => {
  if (typeof value === "string") {
    return (
      <ul>
        {value.split(/[0-9]+\.\s*/).map((point, idx) =>
          point.trim() ? <li key={idx}>{point.trim()}</li> : null
        )}
      </ul>
    );
  }

  return <p>{value || "-"}</p>;
};


  return (
    <div className="container" style={{ marginTop: "80px" }}>
    <Toaster position="top-right" reverseOrder={false} />
      {/* Tital Search and Add */}
      <h2 className="text-primary">Product List</h2>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div className="input-group me-3" style={{ maxWidth: "300px" }}>
          <span className="input-group-text bg-primary text-white">
            <FaSearch />
          </span>
          <input
            type="text"
            className="form-control"
            placeholder="Search products..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <button className="btn btn-success" onClick={handleAddClick}>
          <FaPlus /> Add Product
        </button>
        <label htmlFor="bulk-upload" className="btn btn-info">
            <FaCloudUploadAlt /> Bulk Upload
          </label>
          <input
            type="file"
            id="bulk-upload"
            className="d-none"
            accept=".xlsx"
            onChange={handleFileChange}
          />
          <button
            className="btn btn-primary"
            onClick={handleFileUpload}
            disabled={!selectedFile || isUploading}
          >
              {isUploading ? "Uploading..." : "Upload"}
          </button>
      </div>

      {/* Product Table */}
      <table className="table table-bordered table-striped">
        <thead className="table-primary">
          <tr>
            <th>ID</th>
            <th>Category</th>
            <th>Sub Category</th>
            <th>Name</th>
            <th>Description</th>
            <th>Price</th>
            <th>Dimensions CM</th>
            <th>Dimensions Inch</th>
            <th>Primary Material</th>
            <th>Color</th>
            <th>Weight</th>
            <th>Detail Specifications</th>
            <th>StorageType</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
{currentProducts.map((product, index) => {
  if (!product) return null; // Skip if undefined
  return (
    <tr key={product.ProductId}>
      <td>{index + 1 + (currentPage - 1) * itemsPerPage}</td>
      <td>{product.ProductCategory?.CategoryName || "-"}</td>
              <td>{product.SubCategory?.SubCategoryName || "-"}</td>
              <td>{product.ProductName || "-"}</td>
              <td>{product.ProductDesc || "-"}</td>
              <td>{product.ProductPrice || "-"}</td>
              <td>{renderListField(product.DimensionsCm)}</td>
              <td>{renderListField(product.DimensionsInch)}</td>
              <td>{renderListField(product.PrimaryMaterial)}</td>
              <td>{renderListField(product.ProductColor)}</td>
              <td>{renderListField(product.Weight)}</td>
              <td>
                {console.log("DetailSpecifications Type:", typeof product.DetailSpecifications, product.DetailSpecifications)}
                {typeof product.DetailSpecifications === "string" ? (
                  <ul>
                    {product.DetailSpecifications.split(/[0-9]+\.\s*/).map((point, idx) =>
                      point.trim() ? <li key={idx}>{point.trim()}</li> : null
                    )}
                  </ul>
                ) : (
                  <p>{product.DetailSpecifications || "-"}</p>
                )}
              </td>
              <td>{renderListField(product.StorageType)}</td>
              <td>
                <div className="d-flex gap-2">
                  <button
                    className="btn btn-sm btn-outline-success"
                    onClick={() => handleEditClick(product)}
                  >
                    <FaEdit /> Edit
                  </button>
                  <button
                    className="btn btn-sm btn-outline-danger"
                    onClick={() => handleDeleteClick(product.ProductId)}
                  >
                    <FaTrash /> Delete
                  </button>
                </div>
              </td>
            </tr>
  );
})}
        </tbody>
      </table>

      {/* Pagination */}
      <nav>
        <ul className="pagination justify-content-center">
          {[...Array(totalPages)].map((_, index) => (
            <li
              key={index}
              className={`page-item ${
                currentPage === index + 1 ? "active" : ""
              }`}
            >
              <button
                className="page-link"
                onClick={() => handlePageChange(index + 1)}
              >
                {index + 1}
              </button>
            </li>
          ))}
        </ul>
      </nav>

      {/* Add and edit Product Model */}
      <ProductModal
        show={showModal}
        handleClose={() => setShowModal(false)}
        product={selectedProduct}
        handleSave={handleSaveProduct}
        categories={categories}
      />

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Deletion</Modal.Title>
        </Modal.Header>
        <Modal.Body>Are you sure you want to delete this product?</Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDeleteProduct}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default ProductList;